// Per-alert "Explain" composable.
//
// Today this is a deterministic placeholder so the AlertCard UX (expand,
// loading state, cached result) is fully wired. When the backend
// `/alerts/explain` endpoint ships — proxying to Claude on Bedrock with the
// alert + its evidence + the relevant metric defs — swap the body of
// `fetchExplanation` for a real fetch. The cache, request dedup, and
// component contract stay unchanged.

import { ref } from "vue";
import type { GovernanceAlert } from "@/lib/governanceAlerts";

const cache = new Map<string, string>();
const inFlight = new Map<string, Promise<string>>();

async function fetchExplanation(alert: GovernanceAlert): Promise<string> {
  // Stub: synthesise from the alert payload itself. The shape mirrors what
  // we want from the LLM so the UI doesn't rearrange when we wire it up.
  await new Promise((r) => setTimeout(r, 120)); // simulate async
  const lines = [
    `Why this matters`,
    alert.body,
    ``,
    `Recommended action`,
    `${alert.actionLabel}.`,
  ];
  return lines.join("\n");
}

export function useExplainAlert() {
  const loading = ref<string | null>(null);
  const error = ref<string | null>(null);

  async function explain(alert: GovernanceAlert): Promise<string> {
    if (cache.has(alert.id)) return cache.get(alert.id)!;
    if (inFlight.has(alert.id)) return inFlight.get(alert.id)!;
    loading.value = alert.id;
    error.value = null;
    const p = (async () => {
      try {
        const text = await fetchExplanation(alert);
        cache.set(alert.id, text);
        return text;
      } catch (e) {
        error.value = e instanceof Error ? e.message : String(e);
        throw e;
      } finally {
        if (loading.value === alert.id) loading.value = null;
        inFlight.delete(alert.id);
      }
    })();
    inFlight.set(alert.id, p);
    return p;
  }

  function isLoading(alertId: string): boolean {
    return loading.value === alertId;
  }

  function cached(alertId: string): string | null {
    return cache.get(alertId) ?? null;
  }

  return { explain, isLoading, cached, error };
}
